import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userStorage } from '../../utils/storage';
import { cartUtils } from '../../utils/cartUtils';
import { showToast } from '../../components/toast/ToastContainer';
import { stadiumStorage } from '../../utils/storage';
import { createAnonymousUser } from '../../utils/anonymousUserService';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import CartHeader from './components/CartHeader';
import CartLoadingState from './components/CartLoadingState';
import CartEmptyState from './components/CartEmptyState';
import CartItemsList from './components/CartItemsList';
import PriceInfoWidget from './components/PriceInfoWidget';
import AuthRequiredModal from './components/AuthRequiredModal';
import './CartScreen.css';
import { convertPrice } from '../../utils/currencyConverter';
import { useTranslation } from '../../i18n/i18n';

const CartScreen = ({ isFromHome = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Load cart data from cartUtils (matches Flutter OrderRepository.cart)
  useEffect(() => {
    loadCartData();
    
    // Listen for cart updates
    const handleCartUpdate = () => {
      loadCartData();
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  const loadCartData = () => {
    setLoading(true);
    try {
      // Load cart from storage (matches Flutter loadCart())
      const cart = cartUtils.getCartItems();
      console.log('📦 Loaded cart from storage:', cart);
      setCartItems(cart);
    } catch (error) {
      console.error('❌ Error loading cart:', error);
      setCartItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Update quantity with confirmation dialog for removal (matches Flutter logic exactly)
  const updateQuantity = (foodId, newQuantity) => {
    const item = cartItems.find(item => item.id === foodId);
    if (!item) return;

    // Flutter logic: If current quantity is 1 and trying to decrease, show confirmation
    if (item.quantity === 1 && newQuantity < item.quantity) {
      // Show confirmation dialog before removing (matches Flutter)
      if (window.confirm(`Remove ${item.name} from cart?`)) {
        removeCompletelyFromCart(foodId);
      }
      // If user cancels, do nothing (don't decrease quantity)
    } else {
      // Normal quantity update (matches Flutter RemoveFromCart for quantity > 1)
      try {
        cartUtils.updateQuantity(foodId, newQuantity);
        showToast('success', 'Cart updated');
      } catch (error) {
        console.error('Error updating quantity:', error);
        showToast('error', 'Failed to update cart');
      }
    }
  };

  // Add to cart (increase quantity)
  const addToCart = (foodId) => {
    const item = cartItems.find(item => item.id === foodId);
    if (item) {
      updateQuantity(foodId, item.quantity + 1);
    }
  };

  // Remove completely from cart (matches Flutter RemoveCompletelyFromCart)
  const removeCompletelyFromCart = (foodId) => {
    try {
      cartUtils.removeFromCart(foodId);
      showToast('success', 'Item removed from cart');
    } catch (error) {
      console.error('Error removing from cart:', error);
      showToast('error', 'Failed to remove item');
    }
  };

  // Calculate price totals (matches Flutter OrderRepository calculations)
  const calculateSubtotal = () => {
    return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateDeliveryFee = () => {
    // Handling & Delivery: 2 ILS per regular item, 4 ILS per combo item
    console.log('🛒 Cart items for fee calculation:', cartItems);
    const fee = cartItems.reduce((sum, item) => {
      // Check if item is combo by multiple methods
      const isComboItem = item.isCombo === true || 
                         (item.name && item.name.includes('+')) || 
                         (item.category && item.category.toLowerCase() === 'combo') ||
                         (item.comboItemIds && item.comboItemIds.length > 0);
      
      const itemFee = isComboItem ? 4 : 2;
      console.log(`📦 Cart Item: ${item.name}, isCombo: ${item.isCombo}, detectedCombo: ${isComboItem}, quantity: ${item.quantity}, fee: ${itemFee}`);
      return sum + (itemFee * (item.quantity || 0));
    }, 0);
    console.log('💰 Cart delivery fee calculated:', fee);
    return fee; // ILS
  };

  const calculateTip = () => {
    return 0; // No tip by default (matches Flutter)
  };

  const calculateDiscount = () => {
    return 0; // No discount by default (matches Flutter)
  };

  const calculateTotal = () => {
    // Cart total = subtotal only
    // Delivery fee will be added in order confirmation screen from shop data
    const subtotal = calculateSubtotal();
    return subtotal;
  };

  // Check if any shops are available before allowing order
  const checkShopAvailability = async () => {
    try {
      const stadiumData = stadiumStorage.getSelectedStadium();
      
      // Query shops where shopAvailability == true
      let shopSnap;
      try {
        if (stadiumData?.id) {
          const q = query(
            collection(db, 'shops'),
            where('shopAvailability', '==', true),
            where('stadiumId', '==', stadiumData.id)
          );
          shopSnap = await getDocs(q);
        }
      } catch (_) {}

      if (!shopSnap || shopSnap.empty) {
        // Fallback to availability only
        const qAvail = query(collection(db, 'shops'), where('shopAvailability', '==', true));
        shopSnap = await getDocs(qAvail);
      }

      const availableShops = shopSnap.docs.map(d => ({ id: d.id, data: d.data() }));
      
      if (availableShops.length === 0) {
        showToast('No shops are open right now. Please try again later.', 'error', 5000);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking shop availability:', error);
      showToast('Unable to check shop availability. Please try again.', 'error', 4000);
      return false;
    }
  };

  // Shared checkout logic (used by both regular and guest users)
  const proceedToCheckout = async () => {
    if (cartItems.length === 0) {
      showToast('Cart is empty', 'error', 3000);
      return;
    }
    
    // Check if any shops are available before proceeding
    const shopsAvailable = await checkShopAvailability();
    if (!shopsAvailable) {
      return; // Stop here if no shops are available
    }
    
    // Navigate to tip/checkout screen (matches Flutter)
    navigate('/tip');
  };

  // Handle place order (matches Flutter navigation to tip screen)
  const handlePlaceOrder = async () => {
    // Require sign in before proceeding to checkout flow
    if (!userStorage.isLoggedIn || !userStorage.isLoggedIn()) {
      setShowAuthModal(true);
      return;
    }
    
    proceedToCheckout();
  };

  const handleAuthModalConfirm = () => {
    try { localStorage.setItem('postLoginNext', '/cart'); } catch (_) {}
    setShowAuthModal(false);
    navigate('/auth?next=%2Fcart');
  };

  const handleAuthModalCancel = () => {
    setShowAuthModal(false);
    // Just close the modal, don't navigate anywhere
  };

  const handleContinueAsGuest = async () => {
    try {
      setShowAuthModal(false);
      showToast('info', 'Creating guest account...');
      
      // Create anonymous user and sign them in
      const anonymousUser = await createAnonymousUser();
      
      showToast('success', `Welcome ${anonymousUser.displayName}!`);
      
      // Check if stadium is already selected
      const selectedStadium = stadiumStorage.getSelectedStadium();
      if (selectedStadium) {
        // Stadium already selected, go directly to checkout
        proceedToCheckout();
      } else {
        // No stadium selected, go to stadium selection
        navigate('/stadium-selection');
      }
      
    } catch (error) {
      console.error('Failed to create guest account:', error);
      showToast('error', 'Failed to create guest account. Please try signing in.');
    }
  };

  const handleClearCart = () => {
    if (!cartItems.length) return;
    if (!window.confirm(t('cart.clear_cart_confirm'))) return;
    try {
      cartUtils.clearCart();
      showToast(t('cart.empty'), 'success', 2500);
    } catch (error) {
      console.error('Error clearing cart:', error);
      showToast('Failed to clear cart', 'error', 3000);
    }
  };

  const getCartShopFallback = () => {
    const shopId = cartItems[0]?.shopId;
    return shopId ? `/shop-menu/${shopId}` : '/home';
  };

  if (loading) {
    return <CartLoadingState isFromHome={isFromHome} />;
  }

  return (
    <div className="cart-screen">
      <div className="cart-container">
        {/* Header Component */}
        <CartHeader
          isFromHome={isFromHome}
          fallbackTo={getCartShopFallback()}
          showClearCart={cartItems.length > 0}
          onClearCart={handleClearCart}
        />

        {/* Empty cart state or Cart Items List */}
        {cartItems.length === 0 ? (
          <CartEmptyState />
        ) : (
          <CartItemsList 
            cartItems={cartItems}
            onUpdateQuantity={updateQuantity}
            onAddToCart={addToCart}
            onRemoveFromCart={removeCompletelyFromCart}
          />
        )}
      </div>

      {/* Price Info Widget Component */}
      <PriceInfoWidget 
        cartItems={cartItems}
        calculateSubtotal={calculateSubtotal}
        calculateDeliveryFee={calculateDeliveryFee}
        calculateTip={calculateTip}
        calculateDiscount={calculateDiscount}
        calculateTotal={calculateTotal}
        onPlaceOrder={handlePlaceOrder}
        onContinueAsGuest={handleContinueAsGuest}
        onCancel={handleAuthModalCancel}
        isLoggedIn={userStorage.isLoggedIn()}
      />

      <AuthRequiredModal 
        open={showAuthModal}
        onCancel={handleAuthModalCancel}
        onConfirm={handleAuthModalConfirm}
        onContinueAsGuest={handleContinueAsGuest}
      />
    </div>
  );
};

export default CartScreen;
